import { SearchOptions } from "../../models/search/search-options.model";
import { TCustomerSegment, TLanguage } from "../../types";
import { fetchWith } from "../fetch";

const popularSearchTermsKeyword = "portal-popular-search-terms";

export interface PopularSearchWord {
  name: string;
  url: string;
  segment: string;
}

interface BasePathCustomerSegment {
  en: string;
  de: string;
  it: string;
  fr: string;
}

interface SearchConfiguration {
  defaultBasePath: {
    res: BasePathCustomerSegment;
    sme: BasePathCustomerSegment;
  };
}

const isProductPage = (href: string): boolean => {
  return (
    href.includes("/privatkunden/produkte/") ||
    href.includes("/clients-prives/produits/") ||
    href.includes("/clienti-privati/prodotti/") ||
    href.includes("/residential/products/")
  );
};

const isHelpPage = (href: string): boolean => {
  return (
    href.includes("/privatkunden/hilfe") ||
    href.includes("/clients-prives/aide") ||
    href.includes("/clienti-privati/aiuto") ||
    href.includes("/residential/help")
  );
};

const defaultSearchConfiguration: SearchConfiguration = {
  defaultBasePath: {
    res: {
      en: "/en/residential/find.html#/?segment=residential",
      de: "/de/privatkunden/finden.html#/?segment=residential",
      it: "/it/clienti-privati/trovare.html#/?segment=residential",
      fr: "/fr/clients-prives/trouver.html#/?segment=residential",
    },
    sme: {
      en: "/en/business/sme/find.html#/?segment=business",
      de: "/de/business/kmu/finden.html#/?segment=business",
      it: "/it/business/pmi/trovare.html#/?segment=business",
      fr: "/fr/business/pme/trouver.html#/?segment=business",
    },
  },
};

export class SearchService {
  private static instance: SearchService;
  private config: SearchConfiguration = defaultSearchConfiguration;

  static createQuery(searchTerm: string, path: string, queryParameter: string) {
    if (!searchTerm) {
      return "";
    }

    let query = path.indexOf("?") !== -1 ? "&" : "?";
    query += `${queryParameter}=${encodeURIComponent(searchTerm)}`;

    return query;
  }

  static addQueryParameter(currentQuery: string, key: string, value: string) {
    if (!key || !value) {
      return currentQuery;
    }

    const separator = currentQuery.includes("?") ? "&" : "?";
    const newQueryParameter = `${key}=${encodeURIComponent(value)}`;

    return currentQuery + separator + newQueryParameter;
  }

  static addDestinationTabToQuery(query: string): string {
    if (!query) {
      return "";
    }

    let currentQuery = query;
    const { href } = window.location;
    if (isProductPage(href)) {
      currentQuery = SearchService.addQueryParameter(
        currentQuery,
        "category",
        "products",
      );
    } else if (isHelpPage(href)) {
      currentQuery = SearchService.addQueryParameter(
        currentQuery,
        "category",
        "help",
      );
    }

    return currentQuery;
  }

  private getDefaultBasePath(
    customerSegment: TCustomerSegment,
    language: TLanguage,
  ): string {
    return this.config.defaultBasePath[
      customerSegment === "res" ? "res" : "sme"
    ][language];
  }

  private constructor() {
    // Private constructor -> singleton
  }

  static getInstance(): SearchService {
    if (!SearchService.instance) {
      SearchService.instance = new SearchService();
    }
    return SearchService.instance;
  }

  navigateBySearchTerm(
    searchTerm: string,
    {
      customerSegment,
      hostUrl,
      language,
      queryParameter,
      resultPagePath,
    }: SearchOptions,
  ): void {
    const path =
      resultPagePath || this.getDefaultBasePath(customerSegment, language);
    let query = SearchService.createQuery(searchTerm, path, queryParameter);
    query = SearchService.addDestinationTabToQuery(query);
    let uri = path + query;

    if (hostUrl) {
      uri = hostUrl + uri;
    }

    // navigate to search result page
    window.location.assign(uri);
  }

  async getPopularSearchTerms(language: string): Promise<PopularSearchWord[]> {
    const url = `/portal-services/search/v3/be/api/apps/swisscom_portal_${language}/query/swisscom_portal_${language}`;
    const currentUrl = window.location.href.split("#")[0]?.split("?")[0] || "";
    const currentSegment = window.location.href.match(/.*\/about[\.\/].*/)
      ? "about"
      : window.location.href.match(/.*\/business[\.\/].*/)
        ? "business"
        : "residential";

    let popularSearchTerms = [];
    try {
      const popularSearchTermsRaw = await fetchWith(
        url,
        { q: popularSearchTermsKeyword, loggedIn: "false", currentUrl },
        {},
      );
      popularSearchTerms = popularSearchTermsRaw?.body?.fusion?.widget
        ?.map(JSON.parse)
        ?.map((ps: any) => ps?.config?.links?.[0]?.appendix?.flat())[0]
        ?.filter((psi: any) => psi?.segment === currentSegment)
        ?.filter(Boolean);
    } catch (e) {
      console.log("Popular Search Requests unavailable.", e);
    }

    return popularSearchTerms || [];
  }
}
